import moment from "moment";

// Agent-facing rules for the DS-82 government fee payment link.
// etaapi decides whether a link is allowed; local fee options can also show an
// already-paid state. This module turns etaapi responses into agent-facing text.

// Button states that end the flow until the modal closes or applicant changes.
export const GOV_FEE_LINK_BLOCK = Object.freeze({
  ROUTE_UNAVAILABLE: "ROUTE_UNAVAILABLE",
  GOV_FEE_ALREADY_PAID: "GOV_FEE_ALREADY_PAID",
  ENTRY_ORDER: "ENTRY_ORDER",
});

const ROUTE_UNAVAILABLE_MESSAGE =
  "Payment links are not available yet. Collect the government fee manually through another provider.";

const MESSAGES_BY_CODE = {
  INVALID_PRODUCT:
    "This fee option cannot be paid with a payment link. Choose a Passport Book or Passport Book + Card government fee.",
  GOV_FEE_ALREADY_PAID:
    "Government fee already paid. A new payment link is not needed.",
  ORDER_NOT_PAID:
    "The main order is not paid yet. The customer must pay the main order before the government fee.",
  ENTRY_ORDER:
    "This is an Entry.com order. Use the Entry admin tool to request the government fee.",
  CART_ERROR:
    "The payment system could not create the link. Try again in a few minutes. If it keeps failing, contact the dev team.",
  PROCESSING_MARKUP_CONFIG_UNAVAILABLE:
    "The card processing fee setting could not be loaded, so no link was created. Try again in a few minutes.",
};

const hasJsonBody = (data) =>
  Boolean(data) &&
  typeof data === "object" &&
  (typeof data.code === "string" || typeof data.message === "string");

// Before etaapi ships the route, its catch-all answers 405, and a proxy can answer
// a bare 404. A 404 with a JSON message is the real "applicant not found" answer.
export const isGovFeePaymentLinkRouteMissing = (error) => {
  const status = error?.response?.status;
  if (status === 405) {
    return true;
  }
  return status === 404 && !hasJsonBody(error.response.data);
};

// Returns { message, block } where block is a GOV_FEE_LINK_BLOCK value or null.
export const describeGovFeePaymentLinkError = (error) => {
  if (isGovFeePaymentLinkRouteMissing(error)) {
    return {
      message: ROUTE_UNAVAILABLE_MESSAGE,
      block: GOV_FEE_LINK_BLOCK.ROUTE_UNAVAILABLE,
    };
  }

  const response = error?.response;
  if (!response) {
    return {
      message: `Could not reach the server to create the payment link. ${
        error?.message || ""
      }`.trim(),
      block: null,
    };
  }

  const { status, data } = response;
  const code = data?.code;
  const serverMessage = typeof data?.message === "string" ? data.message : "";

  if (code === "GOV_FEE_ALREADY_PAID" || code === "ENTRY_ORDER") {
    return { message: MESSAGES_BY_CODE[code], block: GOV_FEE_LINK_BLOCK[code] };
  }
  if (code === "FEE_CHARGE_BLOCKED") {
    return {
      message:
        `A payment link cannot be created for this applicant right now. ${serverMessage}`.trim(),
      block: null,
    };
  }
  if (MESSAGES_BY_CODE[code]) {
    return { message: MESSAGES_BY_CODE[code], block: null };
  }
  if (status === 401) {
    return {
      message: "Your session has expired. Please sign in again.",
      block: null,
    };
  }
  if (status === 404) {
    return {
      message: "Applicant not found. Refresh the page and try again.",
      block: null,
    };
  }
  return {
    message: serverMessage || "Could not create the payment link. Try again.",
    block: null,
  };
};

// etaapi sends the fee-link brand as { name, domain } (one cart setting). The name is
// what the agent and customer see; the payment URL host is never used as a brand.
export const getGovFeeLinkBrandName = (link) => {
  const name = link?.brand?.name;
  if (typeof name === "string" && name.trim()) {
    return name.trim();
  }
  return "Entry.com";
};

export const formatGovFeeAmount = (value, currency = "USD") => {
  const amount = Number(value);
  if (value === null || value === undefined || !Number.isFinite(amount)) {
    return "N/A";
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  } catch (error) {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

// The customer may live in any time zone, so the expiry is always stated in UTC.
export const formatGovFeeLinkExpiry = (expiresAt) => {
  const expiry = moment.utc(expiresAt, moment.ISO_8601, true);
  return expiresAt && expiry.isValid()
    ? expiry.format("MMMM D, YYYY [at] HH:mm [UTC]")
    : "";
};

// Text the agent pastes into the customer conversation. It must say that Entry
// (the link brand) processes the government fee, so the customer is not surprised.
export const buildGovFeeCustomerMessage = (link) => {
  const expiresAtText = formatGovFeeLinkExpiry(link?.expiresAt);
  const brandName = getGovFeeLinkBrandName(link);
  const total = formatGovFeeAmount(
    link?.breakdown?.total ?? link?.amount,
    link?.currency,
  );
  const lines = [
    "Hello,",
    "",
    "To continue with your passport renewal, the U.S. Department of State government fee still needs to be paid.",
    `${brandName} processes the government fee for your order. You will see the ${brandName} name on the payment page, on your receipt, and on your card statement.`,
    "",
    `Amount: ${total}`,
    `Payment link: ${link?.url}`,
  ];
  if (expiresAtText) {
    lines.push(`This link expires on ${expiresAtText}.`);
  }
  lines.push(
    "",
    "After you pay, we will continue with your application. If you have any questions, reply to this message and we will help you.",
  );
  return lines.join("\n");
};
